// src/CarbonDashboard.js
// This component is shown ONLY to Track A farmers (those in offset pools)
import React, { useState, useEffect } from 'react';

export default function CarbonDashboard() {
  const [carbonData, setCarbonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCarbonData();
  }, []);

  const fetchCarbonData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/farmers/carbon', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.ok) {
        setCarbonData(data);
      } else {
        setError(data.error || 'Failed to load carbon data');
      }
    } catch (err) {
      console.error('Error fetching carbon data:', err);
      setError('Failed to load carbon data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ background: '#1a3327', padding: '24px', borderRadius: '8px', border: '1px solid #2d5a45' }}>
        <p style={{ color: '#888', margin: 0 }}>Loading carbon data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#1a3327', padding: '24px', borderRadius: '8px', border: '1px solid #2d5a45' }}>
        <p style={{ color: '#ef4444', margin: 0 }}>{error}</p>
      </div>
    );
  }

  if (!carbonData || !carbonData.is_offset_participant) {
    return null; // Don't show anything for non-offset farmers
  }

  const { pools, totals, verification_status } = carbonData;

  // Status badge colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return '#22c55e';
      case 'in_progress': return '#3b82f6';
      case 'pending': return '#f59e0b';
      case 'needs_review': return '#ef4444';
      default: return '#888';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'verified': return 'Verified';
      case 'in_progress': return 'In Progress';
      case 'pending': return 'Pending Review';
      case 'needs_review': return 'Needs Review';
      default: return status;
    }
  };

  return (
    <div style={{ background: '#1a3327', padding: '24px', borderRadius: '8px', border: '2px solid #4ade80' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#4ade80' }}>Carbon Offset Dashboard</h2>
          <p style={{ margin: '8px 0 0 0', color: '#888', fontSize: '14px' }}>
            Track your carbon contribution and verification status
          </p>
        </div>
        <div style={{
          padding: '8px 16px',
          background: getStatusColor(verification_status),
          color: 'white',
          borderRadius: '20px',
          fontSize: '14px',
          fontWeight: 'bold'
        }}>
          {getStatusLabel(verification_status)}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <SummaryCard
          icon="leaf"
          title="Total Carbon"
          value={`${totals?.total_carbon_tonnes?.toFixed(2) || '0.00'}`}
          unit="tonnes CO2"
          color="#4ade80"
        />
        <SummaryCard
          icon="certificate"
          title="VCUs Allocated"
          value={totals?.total_vcus || 0}
          unit="credits"
          color="#60a5fa"
        />
        <SummaryCard
          icon="dollar"
          title="Expected Payment"
          value={`$${totals?.total_expected_payment?.toFixed(2) || '0.00'}`}
          unit="USD"
          color="#fbbf24"
        />
        <SummaryCard
          icon="chart"
          title="Active Pools"
          value={pools?.length || 0}
          unit="pools"
          color="#a78bfa"
        />
      </div>

      {/* Pool Details */}
      {pools && pools.length > 0 && (
        <div>
          <h3 style={{ color: '#4ade80', marginBottom: '16px' }}>Your Offset Pools</h3>
          <div style={{ display: 'grid', gap: '16px' }}>
            {pools.map((pool) => (
              <div
                key={pool.pool_id}
                style={{
                  background: '#0f2419',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #2d5a45'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div>
                    <h4 style={{ margin: '0 0 8px 0', color: '#4ade80' }}>
                      {pool.pool_type === 'biochar' ? 'Biochar' : 'Blue Carbon'} - {pool.pool_name}
                    </h4>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      background: pool.pool_status === 'verified' ? '#22c55e' : pool.pool_status === 'registered' ? '#3b82f6' : '#f59e0b',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {pool.pool_status?.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#4ade80', fontSize: '24px', fontWeight: 'bold' }}>
                      {pool.carbon_tonnes_contributed?.toFixed(2) || '0.00'}
                    </div>
                    <div style={{ color: '#888', fontSize: '12px' }}>tonnes CO2</div>
                  </div>
                </div>

                {/* Pool Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', padding: '16px', background: '#1a3327', borderRadius: '6px' }}>
                  <div>
                    <div style={{ color: '#888', fontSize: '12px' }}>Buffer Held</div>
                    <div style={{ color: '#fbbf24', fontWeight: 'bold' }}>
                      {((pool.buffer_percentage || 0) * 100).toFixed(0)}%
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: '12px' }}>VCUs Allocated</div>
                    <div style={{ color: '#60a5fa', fontWeight: 'bold' }}>
                      {pool.vcus_allocated || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: '12px' }}>Expected Payment</div>
                    <div style={{ color: '#4ade80', fontWeight: 'bold' }}>
                      ${pool.expected_payment_usd?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: '12px' }}>Status</div>
                    <div style={{ color: '#a78bfa', fontWeight: 'bold' }}>
                      {pool.membership_status || 'Active'}
                    </div>
                  </div>
                </div>

                {/* Site Visits */}
                {(pool.last_site_visit || pool.next_site_visit) && (
                  <div style={{ marginTop: '16px', display: 'flex', gap: '24px', fontSize: '13px' }}>
                    {pool.last_site_visit && (
                      <div>
                        <span style={{ color: '#888' }}>Last Visit: </span>
                        <span style={{ color: '#ddd' }}>{new Date(pool.last_site_visit).toLocaleDateString()}</span>
                      </div>
                    )}
                    {pool.next_site_visit && (
                      <div>
                        <span style={{ color: '#888' }}>Next Visit: </span>
                        <span style={{ color: '#4ade80', fontWeight: 'bold' }}>{new Date(pool.next_site_visit).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Buffer Explanation */}
      <div style={{ marginTop: '24px', padding: '16px', background: '#0f2419', borderRadius: '8px', border: '1px solid #2d5a45' }}>
        <h4 style={{ margin: '0 0 8px 0', color: '#fbbf24' }}>About Buffer Credits</h4>
        <p style={{ margin: 0, color: '#888', fontSize: '14px', lineHeight: '1.6' }}>
          A portion of your carbon credits (10-50%) is held in a buffer pool as required by Verra VCS standards. 
          This protects against unforeseen reversals of carbon sequestration. Buffer credits may be released 
          after 5+ years of successful verification, returning up to 15% of held credits every 5 years.
        </p>
      </div>
    </div>
  );
}

function SummaryCard({ icon, title, value, unit, color }) {
  const getIcon = (iconName) => {
    switch(iconName) {
      case 'leaf': return '🌱';
      case 'certificate': return '📜';
      case 'dollar': return '💰';
      case 'chart': return '📊';
      default: return '●';
    }
  };
  
  return (
    <div style={{
      background: '#0f2419',
      padding: '20px',
      borderRadius: '8px',
      border: '1px solid #2d5a45',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '32px', marginBottom: '8px' }}>{getIcon(icon)}</div>
      <div style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>{title}</div>
      <div style={{ color: color, fontSize: '28px', fontWeight: 'bold' }}>{value}</div>
      <div style={{ color: '#666', fontSize: '12px' }}>{unit}</div>
    </div>
  );
}